import { Calendar, Save, Loader2, Copy, ArrowDown, Check, AlertCircle, BookOpen, GraduationCap, Users, Clock, ChevronDown, ChevronUp, Info } from 'lucide-react';
import DepartmentSelector from '../../../components/selectors/DepartmentSelector';
import React, { useState, useEffect, useMemo } from 'react';
import { ApiService } from '../../../services/api';

const ALL_DAYS = ['Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi', 'Pazar'];

interface DepartmentInfo {
  id: string;
  name: string;
  parentName?: string;
  parentId?: string;
}

interface DailyHoursEntry {
  departmentId: string;
  dayOfWeek: string;
  startPeriod: number;
  endPeriod: number;
}

interface TimeSlot {
  id?: string;
  period: string;
  periodNum: number;
  startTime: string;
  endTime: string;
}

interface TeacherAssignment {
  id: string;
  teacherId: string;
  teacher?: { id: string; firstName?: string; lastName?: string; name?: string };
  lessonId: string;
  lesson?: { id: string; name: string };
  classId: string;
  class?: { id: string; name: string };
}

interface Lesson {
  id: string;
  name: string;
  code?: string;
}

const DailyLessons = ({ hidePageHeading = false }: { hidePageHeading?: boolean }) => {
  const [settings, setSettings] = useState<any>(null);
  const [workingDays, setWorkingDays] = useState(5);

  const [timeSlots, setTimeSlots] = useState<TimeSlot[]>([]);
  const [loadingTimeSlots, setLoadingTimeSlots] = useState(false);

  const [departments, setDepartments] = useState<DepartmentInfo[]>([]);
  const [dailyHoursMap, setDailyHoursMap] = useState<Record<string, Record<string, { start: number; end: number }>>>({});
  const [loadingDepts, setLoadingDepts] = useState(false);
  const [savingHours, setSavingHours] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  const [sourceDeptId, setSourceDeptId] = useState<string>("");
  const [targetDeptId, setTargetDeptId] = useState<string>("");
  const [studentDeptId, setStudentDeptId] = useState<string>("");
  const [copyToAll, setCopyToAll] = useState(false);
  const [copying, setCopying] = useState(false);
  const [copyMessage, setCopyMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const [lessons, setLessons] = useState<Lesson[]>([]);
  const [selectedLessonId, setSelectedLessonId] = useState<string>("");
  const [lessonAssignments, setLessonAssignments] = useState<TeacherAssignment[]>([]);
  const [loadingAssignments, setLoadingAssignments] = useState(false);
  const [allDepartmentsMap, setAllDepartmentsMap] = useState<Record<string, string>>({});

  const [selectedFilterDeptId, setSelectedFilterDeptId] = useState<string>("");
  const [activeTab, setActiveTab] = useState<"hours" | "assignments">("hours");
  const [showTimeSlots, setShowTimeSlots] = useState(false);

  const activeDays = useMemo(() => {
    return ALL_DAYS.slice(0, workingDays);
  }, [workingDays]);

  useEffect(() => {
    const init = async () => {
      let settingsData: any = null;
      try {
        const res = await ApiService.getPlanningSettings();
        if (res) {
          settingsData = res;
          setSettings(res);
          setWorkingDays(res.workingDays || 5);
        }
      } catch (e) {
        console.error("Ayarlar alınamadı:", e);
      }
      fetchTimeTables(settingsData);
      fetchDepartments();
      fetchLessons();
    };
    init();
  }, []);

  useEffect(() => {
    if (selectedLessonId) fetchLessonAssignments(selectedLessonId);
    else setLessonAssignments([]);
  }, [selectedLessonId]);

  const addMinutes = (time: string, mins: number): string => {
    const [h, m] = time.split(':').map(Number);
    const total = h * 60 + m + mins;
    return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
  };

  const fetchTimeTables = async (settingsData?: any) => {
    try {
      setLoadingTimeSlots(true);

      const maxLessons = Number(settingsData?.dailyMaxLessons) || 8;
      const lessonDur = Number(settingsData?.lessonDuration) || 40;
      const breakDur = Number(settingsData?.breakBetweenLessons) || 10;
      const startTimeStr = settingsData?.schoolStartTime || '08:30';
      const lunchStartStr = settingsData?.lunchBreakStart || '12:00';
      const lunchDurMin = Number(settingsData?.lunchBreakDuration) || 60;

      const [startH, startM] = startTimeStr.split(':').map(Number);
      const [lunchH, lunchM] = lunchStartStr.split(':').map(Number);
      const lunchStartMinutes = lunchH * 60 + lunchM;
      const lunchEndMinutes = lunchStartMinutes + lunchDurMin;

      const slots: TimeSlot[] = [];
      let currentMinutes = startH * 60 + startM;

      for (let i = 1; i <= maxLessons; i++) {
        const lessonEnd = currentMinutes + lessonDur;
        if (currentMinutes < lunchEndMinutes && lessonEnd > lunchStartMinutes) currentMinutes = lunchEndMinutes;

        const sH = Math.floor(currentMinutes / 60);
        const sM = currentMinutes % 60;
        const endMins = currentMinutes + lessonDur;
        const eH = Math.floor(endMins / 60);
        const eM = endMins % 60;

        slots.push({
          periodNum: i,
          period: `${i}. Saat`,
          startTime: `${String(sH).padStart(2, '0')}:${String(sM).padStart(2, '0')}`,
          endTime: `${String(eH).padStart(2, '0')}:${String(eM).padStart(2, '0')}`,
        });

        currentMinutes = endMins + breakDur;
      }

      setTimeSlots(slots);
    } catch (e) {
      console.error("Ders saatleri alınamadı:", e);
    } finally {
      setLoadingTimeSlots(false);
    }
  };

  const fetchDepartments = async () => {
    try {
      setLoadingDepts(true);

      const settingsRes = await ApiService.getSettings();
      const s = settingsRes?.data ?? settingsRes;
      const studentDeptId = s?.studentDepartmentId;
      setStudentDeptId(studentDeptId);
      if (!studentDeptId) {
        console.warn("Student department ID not found in settings");
        setLoadingDepts(false);

        return;
      };

      const categoriesRes = await ApiService.getDepartments(1, 100, `&parentId=${studentDeptId}`);
      const categories = parseListResponse(categoriesRes);

      const deptMap: Record<string, string> = {};
      const leafDepts: DepartmentInfo[] = [];

      if (Array.isArray(categories)) {
        categories.forEach((cat: any) => {
          deptMap[cat.id] = cat.name;
        });

        const promises = categories.map((cat: any) =>
          ApiService.getDepartments(1, 100, `&parentId=${cat.id}`)
            .then(res => {
              const list = parseListResponse(res);
              if (Array.isArray(list)) {
                return list.map((l: any) => ({
                  ...l,
                  parentName: cat.name
                }));
              };

              return [];
            })
            .catch(() => [])
        );

        const results = await Promise.all(promises);
        results.forEach(list => {
          list.forEach((cls: any) => {
            leafDepts.push({
              id: cls.id,
              name: cls.name,
              parentName: cls.parentName,
              parentId: cls.parentId
            });

            deptMap[cls.id] = cls.name;
          });
        });
      };

      setDepartments(leafDepts);
      setAllDepartmentsMap(deptMap);

      await fetchAllDailyHours();
    } catch (e) {
      console.error("Departmanlar yüklenemedi:", e);
    } finally {
      setLoadingDepts(false);
    }
  };

  const fetchAllDailyHours = async () => {
    try {
      const res = await ApiService.getDepartmentDailyHours({});
      const data = parseListResponse(res);

      const map: Record<string, Record<string, { start: number; end: number }>> = {};
      data.forEach((item: any) => {
        if (!map[item.departmentId]) map[item.departmentId] = {};

        map[item.departmentId][item.dayOfWeek] = {
          start: item.startPeriod || 0,
          end: item.endPeriod || 0,
        };
      });

      setDailyHoursMap(map);
    } catch (e) {
      console.error("Ders saatleri yüklenemedi:", e);
    }
  };

  const fetchLessons = async () => {
    try {
      const res = await ApiService.getPlanningLessons({ limit: 100, isActive: true });
      const data = parseListResponse(res);

      setLessons(data.map((l: any) => ({ id: l.id, name: l.name, code: l.code })));
    } catch (e) {
      console.error("Dersler yüklenemedi:", e);
    }
  };

  const fetchLessonAssignments = async (lessonId: string) => {
    try {
      setLoadingAssignments(true);

      const res = await ApiService.getTeacherAssignments({ lessonId, limit: 100 });
      const data = parseListResponse(res);

      setLessonAssignments(data);
    } catch (e) {
      console.error("Ders atamaları yüklenemedi:", e);
      setLessonAssignments([]);
    } finally {
      setLoadingAssignments(false);
    }
  };

  const parseListResponse = (res: any) => {
    const data = (res?.data ?? res) as any;

    if (Array.isArray(data)) return data;
    if (data?.data && Array.isArray(data.data)) return data.data;
    if (data?.results && Array.isArray(data.results)) return data.results;

    return [];
  };

  const handleHoursChange = (deptId: string, day: string, field: "start" | "end", value: string) => {
    const numVal = parseInt(value) || 0;
    setDailyHoursMap(prev => {
      const newMap = { ...prev };

      if (!newMap[deptId]) newMap[deptId] = {};
      if (!newMap[deptId][day]) newMap[deptId][day] = { start: 0, end: 0 };

      newMap[deptId][day] = {
        ...newMap[deptId][day],
        [field]: numVal
      };

      return newMap;
    });

    setHasChanges(true);
  };

  const saveAllHours = async () => {
    try {
      setSavingHours(true);

      const bulkData = Object.entries(dailyHoursMap).map(([deptId, days]) => ({
        departmentId: deptId,
        days: Object.entries(days).map(([dayOfWeek, values]) => ({
          dayOfWeek,
          startPeriod: values.start,
          endPeriod: values.end,
        })),
      }));

      if (bulkData.length > 0) await ApiService.bulkUpdateDepartmentDailyHours({ data: bulkData });

      setHasChanges(false);
    } catch (e) {
      console.error("Kaydetme hatası:", e);
    } finally {
      setSavingHours(false);
    }
  };

  const handleCopy = async () => {
    if (!sourceDeptId) {
      setCopyMessage({ type: 'error', text: 'Lütfen kaynak departman seçin.' });

      return;
    };

    if (!copyToAll && !targetDeptId) {
      setCopyMessage({ type: 'error', text: 'Lütfen hedef departman seçin veya "Tüm sınıflar"ı işaretleyin.' });

      return;
    };

    try {
      setCopying(true);
      setCopyMessage(null);

      await ApiService.copyDepartmentDailyHours({
        sourceDepartmentId: sourceDeptId,
        targetDepartmentId: targetDeptId || undefined,
        copyToAll
      });

      setCopyMessage({ type: 'success', text: 'Saatler başarıyla kopyalandı.' });
      await fetchAllDailyHours();
    } catch (e: any) {
      console.error("Kopyalama hatası:", e);

      setCopyMessage({ type: 'error', text: e?.response?.data?.message || e?.message || 'Kopyalama sırasında bir hata oluştu.' });
    } finally {
      setCopying(false);
    }
  };

  const getTeacherName = (t: any) => {
    if (!t) return "İsimsiz";
    if (t.firstName && t.lastName) return `${t.firstName} ${t.lastName}`;

    return t.name || "İsimsiz";
  };

  const getClassNameForAssignment = (assignment: TeacherAssignment) => {
    if (assignment.classId && allDepartmentsMap[assignment.classId]) return allDepartmentsMap[assignment.classId];
    if (assignment.class?.name) return assignment.class.name;

    return assignment.classId ? assignment.classId.substring(0, 8) + "..." : "?";
  };

  const groupedAssignments = useMemo(() => {
    const map: Record<string, { teacher: any; classes: string[] }> = {};

    lessonAssignments.forEach(a => {
      const teacherId = a.teacher?.id || a.teacherId;

      if (!map[teacherId]) {
        map[teacherId] = {
          teacher: a.teacher,
          classes: []
        };
      };

      const className = getClassNameForAssignment(a);
      if (!map[teacherId].classes.includes(className)) map[teacherId].classes.push(className);
    });

    return Object.values(map);
  }, [lessonAssignments, allDepartmentsMap]);

  const groupedDepartments = useMemo(() => {
    let filteredDepts = departments;

    if (selectedFilterDeptId) {
      const exactMatch = departments.find(d => d.id === selectedFilterDeptId);

      if (exactMatch) {
        filteredDepts = [exactMatch];
      } else {
        const children = departments.filter(d => d.parentId === selectedFilterDeptId);
        if (children.length > 0) {
          filteredDepts = children;
        } else {
          filteredDepts = [];
        }
      }
    }

    const groups: Record<string, DepartmentInfo[]> = {};
    filteredDepts.forEach(dept => {
      const groupName = dept.parentName || "Diğer";
      if (!groups[groupName]) groups[groupName] = [];
      groups[groupName].push(dept);
    });
    return groups;
  }, [departments, selectedFilterDeptId]);

  return (
    <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
      {!hidePageHeading && (
        <div className="border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white px-4 py-4 sm:px-6">
          <div className="min-w-0">
            <h2 className="text-lg font-bold text-gray-900 sm:text-xl">Günlük Ders Saatleri</h2>
            <p className="mt-0.5 text-sm text-gray-500">Sınıf saatleri ve ders atamalarını yönetin</p>
          </div>
        </div>
      )}
      <div className="border-b border-gray-200 bg-white px-4 sm:px-6">
        <nav className="flex flex-wrap gap-x-6 gap-y-1" aria-label="Günlük ders bölümleri">
          <button
            type="button"
            onClick={() => setActiveTab("hours")}
            className={`relative pb-3 pt-3 text-sm font-medium transition-colors ${
              activeTab === "hours"
                ? "text-blue-600 after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:rounded-full after:bg-blue-500"
                : "text-gray-500 hover:text-gray-900"
            }`}>
            <span className="inline-flex items-center gap-2">
              <Calendar size={16} className="shrink-0 opacity-80" aria-hidden />
              Günlük Ders Saatleri
            </span>
          </button>
          <button
            type="button"
            onClick={() => setActiveTab("assignments")}
            className={`relative pb-3 pt-3 text-sm font-medium transition-colors ${
              activeTab === "assignments"
                ? "text-blue-600 after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:rounded-full after:bg-blue-500"
                : "text-gray-500 hover:text-gray-900"
            }`}>
            <span className="inline-flex items-center gap-2">
              <BookOpen size={16} className="shrink-0 opacity-80" aria-hidden />
              Ders Atamaları
            </span>
          </button>
        </nav>
      </div>

      <div className="p-4 sm:p-6">
      {activeTab === "hours" && (
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">

          <div className="lg:col-span-1 space-y-4">

            <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
              <div className="rounded-t-xl border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white px-4 py-3.5">
                <div className="flex items-center gap-2">
                  <div className="rounded-lg bg-blue-100 p-1.5">
                    <Calendar size={14} className="text-blue-600" aria-hidden />
                  </div>
                  <h3 className="text-sm font-semibold text-gray-800">Ders saati özeti</h3>
                </div>
              </div>
              <div className="p-4">
                <div className="grid grid-cols-2 gap-3">
                  <div className="rounded-lg border border-gray-200 bg-gray-50/90 p-3 text-center">
                    <p className="text-2xl font-bold tabular-nums text-gray-900">{settings?.dailyMaxLessons || timeSlots.length}</p>
                    <p className="mt-1 text-[10px] font-medium uppercase tracking-wider text-gray-500">Ders saati</p>
                  </div>
                  <div className="rounded-lg border border-gray-200 bg-gray-50/90 p-3 text-center">
                    <p className="text-2xl font-bold tabular-nums text-gray-900">{workingDays}</p>
                    <p className="mt-1 text-[10px] font-medium uppercase tracking-wider text-gray-500">Çalışma günü</p>
                  </div>
                </div>
              </div>
              <div className="border-t border-gray-100 bg-white px-4 py-3">
                <div className="flex items-start gap-2">
                  <Info size={14} className="mt-0.5 shrink-0 text-blue-500" aria-hidden />
                  <p className="text-xs leading-relaxed text-gray-500">
                    Her sınıf için hangi ders saatlerinde gireceğini belirleyin. Örn:{" "}
                    <span className="font-medium text-gray-700">3 – 4</span> → 3. ve 4. ders saatlerinde girer.
                  </p>
                </div>
              </div>
            </div>

            {timeSlots.length > 0 && (
              <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
                <button
                  type="button"
                  onClick={() => setShowTimeSlots(!showTimeSlots)}
                  className="flex w-full items-center justify-between border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white px-4 py-3.5 text-left transition-colors hover:bg-gray-50/80">
                  <div className="flex items-center gap-2">
                    <div className="rounded-lg bg-amber-100 p-1.5">
                      <Clock size={14} className="text-amber-600" aria-hidden />
                    </div>
                    <span className="text-sm font-semibold text-gray-800">Ders saati süreleri</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-500">
                      {settings?.dailyMaxLessons || timeSlots.length} saat
                    </span>
                    {showTimeSlots ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
                  </div>
                </button>
                {showTimeSlots && (
                  <div className="border-t border-gray-100 px-2 py-2">
                    <div className="max-h-[300px] space-y-0.5 overflow-y-auto">
                      {timeSlots.map((slot) => (
                        <div
                          key={slot.periodNum}
                          className="group flex items-center justify-between rounded-lg px-2.5 py-1.5 transition-colors hover:bg-gray-50">
                          <div className="flex items-center gap-2.5">
                            <span className="flex h-6 w-6 items-center justify-center rounded-md bg-blue-100 text-xs font-bold text-blue-700 transition-colors group-hover:bg-blue-200">
                              {slot.periodNum}
                            </span>
                            <span className="text-sm text-gray-700">{slot.periodNum}. ders</span>
                          </div>
                          <span className="rounded bg-gray-100 px-2 py-0.5 font-mono text-xs text-gray-500">
                            {slot.startTime} – {slot.endTime}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="bg-white rounded-xl shadow-sm border border-gray-200">
              <div className="bg-gradient-to-r from-gray-50 to-white p-3.5 border-b border-gray-100 rounded-t-xl">
                <div className="flex items-center space-x-2">
                  <div className="bg-emerald-100 p-1.5 rounded-lg">
                    <Copy size={14} className="text-emerald-600" />
                  </div>
                  <h3 className="font-semibold text-sm text-gray-800">Saatleri Kopyala</h3>
                </div>
              </div>

              <div className="p-3.5 space-y-3">
                <div>
                  <label className="block text-[11px] font-medium text-gray-500 uppercase tracking-wider mb-1.5">Kaynak Departman</label>
                  {loadingDepts ? (
                    <div className="h-9 bg-gray-50 animate-pulse rounded-lg border border-gray-200"></div>
                  ) : (
                    <DepartmentSelector
                      value={sourceDeptId}
                      onChange={setSourceDeptId}
                      placeholder="Kaynak seçiniz..."
                      className="w-full text-sm"
                      rootId={studentDeptId}
                    />
                  )}
                </div>

                <div className="flex justify-center py-0.5">
                  <div className="bg-gray-100 p-1 rounded-full">
                    <ArrowDown size={14} className="text-gray-400" />
                  </div>
                </div>

                <div>
                  <label className="block text-[11px] font-medium text-gray-500 uppercase tracking-wider mb-1.5">Hedef Departman</label>
                  {loadingDepts ? (
                    <div className="h-9 bg-gray-50 animate-pulse rounded-lg border border-gray-200"></div>
                  ) : (
                    <DepartmentSelector
                      value={targetDeptId}
                      onChange={setTargetDeptId}
                      placeholder="Hedef seçiniz..."
                      className="w-full text-sm"
                      disabled={copyToAll}
                      rootId={studentDeptId}
                    />
                  )}
                </div>

                <label className="flex items-center space-x-2 cursor-pointer py-1">
                  <input
                    type="checkbox"
                    checked={copyToAll}
                    onChange={(e) => setCopyToAll(e.target.checked)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 h-4 w-4"
                  />
                  <span className="text-sm text-gray-600">Tüm departmanlara kopyala</span>
                </label>

                {copyMessage && (
                  <div className={`p-2.5 rounded-lg text-xs flex items-start space-x-2 ${copyMessage.type === 'success'
                    ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                    : 'bg-red-50 text-red-700 border border-red-200'
                    }`}>
                    {copyMessage.type === 'success' ? <Check size={14} className="mt-0.5 shrink-0" /> : <AlertCircle size={14} className="mt-0.5 shrink-0" />}
                    <span>{copyMessage.text}</span>
                  </div>
                )}

                <button
                  type="button"
                  onClick={handleCopy}
                  disabled={copying || !sourceDeptId}
                  className={`flex w-full items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-medium transition-colors ${
                    copying || !sourceDeptId
                      ? "cursor-not-allowed bg-gray-100 text-gray-400"
                      : "bg-blue-600 text-white shadow-sm hover:bg-blue-700"
                  }`}>
                  {copying ? <Loader2 size={16} className="animate-spin" /> : <Copy size={16} />}
                  <span>{copying ? "Kopyalanıyor..." : "Kopyala"}</span>
                </button>
              </div>
            </div>
          </div>

          <div className="lg:col-span-3">
            <div className="flex h-full min-h-0 flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
              <div className="border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white px-4 py-4 sm:px-6">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex min-w-0 flex-col">
                    {!hidePageHeading && (
                      <h2 className="text-lg font-bold text-gray-900 sm:text-xl">Günlük Ders Saatleri</h2>
                    )}
                    <span className={`text-xs text-gray-500 sm:text-sm ${hidePageHeading ? "" : "mt-0.5"}`}>
                      {timeSlots.length} ders saati tanımlı · Sınıfların günlük ders saatlerini ayarlayın.
                    </span>
                  </div>

                  <div className="flex flex-col items-stretch gap-2 sm:flex-row sm:items-center sm:gap-3">
                    <div className="w-full sm:w-64">
                      <DepartmentSelector
                        value={selectedFilterDeptId}
                        onChange={setSelectedFilterDeptId}
                        placeholder="Sınıf/Grup filtrele..."
                        rootId={studentDeptId}
                        allowClear={true}
                        className="text-sm"
                      />
                    </div>

                    <div className="flex items-center gap-2 sm:gap-3">
                      {hasChanges && (
                        <span className="hidden text-sm font-medium text-amber-600 sm:inline">Kaydedilmedi</span>
                      )}
                      <button
                        type="button"
                        onClick={saveAllHours}
                        disabled={!hasChanges || savingHours}
                        className={`flex w-full items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors sm:w-auto ${
                          hasChanges
                            ? "bg-green-600 text-white hover:bg-green-700"
                            : "cursor-not-allowed bg-gray-100 text-gray-400"
                        }`}>
                        {savingHours ? <Loader2 className="animate-spin" size={18} /> : <Save size={18} />}
                        <span>{savingHours ? "Kaydediliyor..." : "Kaydet"}</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div className="min-h-0 flex-1 p-4 sm:p-6">
                {loadingDepts || loadingTimeSlots ? (
                  <div className="flex h-64 items-center justify-center">
                    <Loader2 className="animate-spin text-blue-600" size={32} />
                  </div>
                ) : departments.length === 0 ? (
                  <div className="flex h-64 flex-col items-center justify-center text-gray-400">
                    <div className="mb-3 rounded-full bg-gray-100 p-4">
                      <Calendar size={32} />
                    </div>
                    <p className="text-center text-sm">Departman bulunamadı. Ayarlardan öğrenci departmanını kontrol edin.</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <div className="max-h-[calc(100vh-340px)] min-h-[300px] overflow-y-auto">
                      <table className="w-full border-collapse">
                        <thead className="sticky top-0 z-10">
                          <tr className="bg-gray-50">
                            <th className="sticky left-0 z-20 min-w-[40px] border border-gray-200 bg-gray-50 px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                              #
                            </th>
                            <th className="sticky left-[40px] z-20 min-w-[120px] border border-gray-200 bg-gray-50 px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                              Sınıf adı
                            </th>
                            {activeDays.map((day) => (
                              <th
                                key={day}
                                className="min-w-[120px] border border-gray-200 px-2 py-2 text-center text-xs font-medium uppercase tracking-wider text-gray-500 transition-colors"
                                colSpan={2}>
                                {day}
                              </th>
                            ))}
                          </tr>
                          <tr className="bg-gray-50">
                            <th className="sticky left-0 z-20 border border-gray-200 bg-gray-50" />
                            <th className="sticky left-[40px] z-20 border border-gray-200 bg-gray-50" />
                            {activeDays.map((day) => (
                              <React.Fragment key={`sub-${day}`}>
                                <th className="border border-gray-200 px-1 py-1 text-center text-[10px] font-medium text-gray-400 transition-colors">
                                  Başl.
                                </th>
                                <th className="border border-gray-200 px-1 py-1 text-center text-[10px] font-medium text-gray-400 transition-colors">
                                  Bit.
                                </th>
                              </React.Fragment>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="bg-white">
                          {Object.entries(groupedDepartments).map(([groupName, depts]) => (
                            <React.Fragment key={groupName}>
                              <tr className="bg-gray-50/90">
                                <td
                                  colSpan={2 + activeDays.length * 2}
                                  className="sticky left-0 z-10 border border-gray-200 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-gray-700">
                                  {groupName}
                                </td>
                              </tr>
                              {depts.map((dept, idx) => {
                                const deptHours = dailyHoursMap[dept.id] || {};
                                return (
                                  <tr key={dept.id} className="transition-colors hover:bg-gray-50/50">
                                    <td className="sticky left-0 z-10 border border-gray-200 bg-white px-3 py-1.5 text-center text-xs text-gray-500">
                                      {idx + 1}
                                    </td>
                                    <td className="sticky left-[40px] z-10 whitespace-nowrap border border-gray-200 bg-white px-3 py-1.5 text-sm font-medium text-gray-900">
                                      {dept.name}
                                    </td>
                                    {activeDays.map((day) => {
                                      const dayData = deptHours[day] || { start: 0, end: 0 };
                                      return (
                                        <React.Fragment key={`${dept.id}-${day}`}>
                                          <td className="border border-gray-200 px-1 py-1 text-center transition-colors">
                                            <input
                                              type="number"
                                              min="0"
                                              max={timeSlots.length}
                                              value={dayData.start || ""}
                                              onChange={(e) => handleHoursChange(dept.id, day, "start", e.target.value)}
                                              className="h-7 w-12 rounded border border-gray-300 text-center text-sm [appearance:textfield] focus:border-blue-500 focus:ring-2 focus:ring-blue-500 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                                              placeholder="0"
                                            />
                                          </td>
                                          <td className="border border-gray-200 px-1 py-1 text-center transition-colors">
                                            <input
                                              type="number"
                                              min="0"
                                              max={timeSlots.length}
                                              value={dayData.end || ""}
                                              onChange={(e) => handleHoursChange(dept.id, day, "end", e.target.value)}
                                              className="h-7 w-12 rounded border border-gray-300 text-center text-sm [appearance:textfield] focus:border-blue-500 focus:ring-2 focus:ring-blue-500 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                                              placeholder="0"
                                            />
                                          </td>
                                        </React.Fragment>
                                      );
                                    })}
                                  </tr>
                                );
                              })}
                            </React.Fragment>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50 px-3 py-3">
                      <p className="text-xs text-gray-600">
                        <span className="font-medium text-gray-700">Not:</span> İlk kutu başlangıç ders saati numarası, ikinci kutu
                        bitiş ders saati numarasıdır. Ders 1 ile {timeSlots.length} arasında toplam {timeSlots.length} saat.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === "assignments" && (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-4">
          <div className="lg:col-span-1">
            <div className="flex h-full min-h-0 flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
              <div className="border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white px-4 py-3">
                <div className="flex items-center gap-2">
                  <div className="rounded-lg bg-blue-100 p-1.5">
                    <BookOpen size={14} className="text-blue-600" aria-hidden />
                  </div>
                  <h3 className="text-sm font-semibold text-gray-800">Ders seçimi</h3>
                </div>
              </div>
              <div className="max-h-[min(600px,55vh)] flex-1 space-y-2 overflow-y-auto p-4 lg:max-h-[600px]">
                {lessons.map((lesson) => (
                  <button
                    key={lesson.id}
                    type="button"
                    onClick={() => setSelectedLessonId(lesson.id)}
                    className={`w-full rounded-lg border p-3 text-left transition-colors ${
                      selectedLessonId === lesson.id
                        ? "border-blue-300 bg-blue-50 text-blue-900 shadow-sm"
                        : "border-gray-200 bg-white hover:bg-gray-50"
                    }`}>
                    <div className="flex items-center gap-2">
                      <BookOpen size={16} className="shrink-0 text-blue-500" aria-hidden />
                      <span className="text-sm font-medium">{lesson.name}</span>
                    </div>
                    {lesson.code && <span className="ml-8 text-xs text-gray-400">{lesson.code}</span>}
                  </button>
                ))}
                {lessons.length === 0 && <div className="py-4 text-center text-sm text-gray-500">Ders bulunamadı.</div>}
              </div>
            </div>
          </div>

          <div className="lg:col-span-3">
            <div className="flex min-h-[20rem] flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm lg:min-h-0 lg:h-full">
              {!selectedLessonId ? (
                <div className="flex flex-1 flex-col items-center justify-center px-6 py-16 text-gray-400">
                  <BookOpen size={48} className="mb-3 opacity-30" aria-hidden />
                  <p className="text-lg font-medium text-gray-600">Ders seçin</p>
                  <p className="mt-1 max-w-sm text-center text-sm text-gray-500">
                    Atanan öğretmenleri görmek için soldaki listeden bir ders seçin.
                  </p>
                </div>
              ) : (
                <>
                  <div className="border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white px-4 py-4 sm:px-6">
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                      <div className="min-w-0">
                        {!hidePageHeading && <h2 className="text-lg font-bold text-gray-900 sm:text-xl">Ders atamaları</h2>}
                        <span className="text-xs text-gray-500 sm:text-sm">
                          {lessons.find((l) => l.id === selectedLessonId)?.name} · Atanan öğretmenler
                        </span>
                      </div>
                      <div className="w-fit shrink-0 rounded-full bg-blue-50 px-3 py-1 text-xs font-medium text-blue-800 ring-1 ring-blue-100 sm:text-sm">
                        {groupedAssignments.length} öğretmen
                      </div>
                    </div>
                  </div>
                  <div className="flex-1 p-4 sm:p-6">
                    {loadingAssignments ? (
                      <div className="flex h-32 items-center justify-center">
                        <Loader2 className="animate-spin text-blue-600" size={32} />
                      </div>
                    ) : groupedAssignments.length === 0 ? (
                      <div className="flex h-32 flex-col items-center justify-center text-gray-400">
                        <GraduationCap size={32} className="mb-2 opacity-30" />
                        <p className="text-sm">Bu derse atanmış öğretmen bulunmamaktadır.</p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                        {groupedAssignments.map((group, index) => (
                          <div
                            key={index}
                            className="rounded-xl border border-gray-200 bg-gray-50/80 p-4 transition-shadow hover:shadow-md">
                            <div className="mb-3 flex items-center gap-3">
                              <div className="rounded-full bg-blue-100 p-2">
                                <GraduationCap size={20} className="text-blue-600" />
                              </div>
                              <div className="min-w-0">
                                <p className="font-semibold text-gray-900">{getTeacherName(group.teacher)}</p>
                                <p className="text-xs text-gray-500">{group.classes.length} sınıf</p>
                              </div>
                            </div>
                            <div className="flex flex-wrap gap-1.5">
                              {group.classes.map((cls, i) => (
                                <span
                                  key={i}
                                  className="inline-flex items-center gap-1 rounded-md border border-gray-200 bg-white px-2 py-0.5 text-xs font-medium text-gray-700">
                                  <Users size={10} className="text-gray-400" aria-hidden />
                                  <span>{cls}</span>
                                </span>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default DailyLessons;